import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FaServer, FaUserClock, FaUserSlash, FaCalendarAlt } from 'react-icons/fa';
import { BLEEP_SERVER_IDS, BLEEP_SERVER_NAMES } from '../utils/constants';
import PurgatoryReviewTab from '../components/onboarding/PurgatoryReviewTab';
import EmployeeInactivityTab from '../components/onboarding/EmployeeInactivityTab';
import MonthlyReportPanel from '../components/onboarding/MonthlyReportPanel';
import GlobalPurgatoryOverview from '../components/onboarding/GlobalPurgatoryOverview';

const OnboardingPurgatoryPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Determine initial server from URL search params, location state, or default to first server
    const paramServer = searchParams.get('server') || location.state?.serverId;
    const initialServer = BLEEP_SERVER_IDS.includes(paramServer) ? paramServer : BLEEP_SERVER_IDS[0];

    // Determine initial tab from URL search params
    const paramTab = searchParams.get('tab');
    const initialTab = ['purgatory', 'inactivity', 'reports'].includes(paramTab) ? paramTab : 'purgatory';

    const [selectedServer, setSelectedServer] = useState(initialServer);
    const [activeTab, setActiveTab] = useState(initialTab);

    // Sync with browser Back/Forward navigation changes in searchParams
    useEffect(() => {
        const s = searchParams.get('server');
        if (s && BLEEP_SERVER_IDS.includes(s) && s !== selectedServer) {
            setSelectedServer(s);
        }
        const t = searchParams.get('tab');
        if (t && ['purgatory', 'inactivity', 'reports'].includes(t) && t !== activeTab) {
            setActiveTab(t);
        }
    }, [searchParams, selectedServer, activeTab]);

    // Sync state changes with URL query parameters for bookmarking & browser reload persistence
    const handleServerChange = (newServer) => {
        setSelectedServer(newServer);
        setSearchParams({ server: newServer, tab: activeTab }, { replace: true });
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setSearchParams({ server: selectedServer, tab: newTab }, { replace: true });
    };

    return (
        <div className="page-container" style={{ maxWidth: '1500px', margin: '0 auto', paddingBottom: '40px' }}>
            {/* 1. Page Header & Server Selector */}
            <div 
                className="page-header" 
                style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    flexWrap: 'wrap', 
                    gap: '16px',
                    marginBottom: '24px' 
                }}
            >
                <div>
                    <h1 style={{ color: '#2d3748', margin: '0 0 6px 0', fontSize: '28px', fontWeight: '700' }}>
                        Onboarding360 Purgatory
                    </h1>
                    <p style={{ color: '#718096', margin: 0, fontSize: '14px' }}>
                        Review incoming applicant changes from Onboarding360 and manage employee inactivity maintenance.
                    </p>
                </div>

                {/* Server Switcher Card */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaServer style={{ color: '#2b6cb0', fontSize: '14px' }} />
                        <label 
                            htmlFor="purgatory-server-select" 
                            style={{ 
                                fontSize: '13px', 
                                fontWeight: '700', 
                                color: '#4a5568', 
                                textTransform: 'uppercase', 
                                letterSpacing: '0.5px' 
                            }}
                        >
                            Franchise Server:
                        </label>
                    </div>
                    <select
                        id="purgatory-server-select"
                        value={selectedServer}
                        onChange={(e) => handleServerChange(e.target.value)}
                        style={{
                            padding: '8px 14px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e0',
                            backgroundColor: '#ffffff',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2b6cb0',
                            cursor: 'pointer',
                            outline: 'none',
                        }}
                    >
                        {BLEEP_SERVER_IDS.map((sid) => (
                            <option key={sid} value={sid}>
                                {BLEEP_SERVER_NAMES[sid] || sid} ({sid})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* 2. Global Multi-Franchise Overview */}
            <GlobalPurgatoryOverview
                selectedServer={selectedServer}
                onSelectServer={handleServerChange}
            />

            {/* 3. Top Navigation Tabs */}
            <div style={{
                display: 'flex',
                gap: '12px',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '24px'
            }}>
                <button
                    type="button"
                    onClick={() => handleTabChange('purgatory')}
                    style={{
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: '700',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'purgatory' ? '#2b6cb0' : '#718096',
                        borderBottom: activeTab === 'purgatory' ? '3px solid #2b6cb0' : '3px solid transparent',
                        marginBottom: '-2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.15s ease-in-out'
                    }}
                >
                    <FaUserClock style={{ fontSize: '15px' }} />
                    <span>Candidate Purgatory</span>
                </button>

                <button
                    type="button"
                    onClick={() => handleTabChange('inactivity')}
                    style={{
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: '700',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'inactivity' ? '#2b6cb0' : '#718096',
                        borderBottom: activeTab === 'inactivity' ? '3px solid #2b6cb0' : '3px solid transparent',
                        marginBottom: '-2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.15s ease-in-out'
                    }}
                >
                    <FaUserSlash style={{ fontSize: '15px' }} />
                    <span>Employee Inactivity</span>
                </button>

                <button
                    type="button"
                    onClick={() => handleTabChange('reports')}
                    style={{
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: '700',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'reports' ? '#2b6cb0' : '#718096',
                        borderBottom: activeTab === 'reports' ? '3px solid #2b6cb0' : '3px solid transparent',
                        marginBottom: '-2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.15s ease-in-out'
                    }}
                >
                    <FaCalendarAlt style={{ fontSize: '15px' }} />
                    <span>Monthly Reports & Licences</span>
                </button>
            </div>

            {/* 3. Tab Container (keyed by selectedServer to force clean remount on server switch) */}
            <div key={selectedServer}>
                {activeTab === 'purgatory' && (
                    <PurgatoryReviewTab key={selectedServer} serverID={selectedServer} />
                )}

                {activeTab === 'inactivity' && (
                    <EmployeeInactivityTab key={`inactivity-${selectedServer}`} serverID={selectedServer} />
                )}

                {activeTab === 'reports' && (
                    <MonthlyReportPanel key={`reports-${selectedServer}`} serverID={selectedServer} />
                )}
            </div>
        </div>
    );
};

export default OnboardingPurgatoryPage;
